import { create } from 'zustand';
import { isApiEnabled } from '../api/config';
import { profileApi, type UserProfile } from '../features/profile/api';
import {
  DEFAULT_LOCALE,
  DEFAULT_MEASUREMENT_REGION,
  type MeasurementRegion,
  type SupportedLocale,
  getStoredLocale,
  getStoredMeasurementRegion,
  normalizeLocale,
  normalizeMeasurementRegion,
  setStoredLocale,
  setStoredMeasurementRegion,
} from '../i18n/locale';

interface LocaleState {
  locale: SupportedLocale;
  measurementRegion: MeasurementRegion;
  hydrated: boolean;
  saving: boolean;
  error: string | null;
  applyProfile: (profile: UserProfile) => void;
  loadFromProfile: () => Promise<void>;
  setLocale: (locale: SupportedLocale) => Promise<void>;
  setMeasurementRegion: (region: MeasurementRegion) => Promise<void>;
}

function persistLocal(locale: SupportedLocale, region: MeasurementRegion) {
  setStoredLocale(locale);
  setStoredMeasurementRegion(region);
}

async function syncProfile(locale: SupportedLocale, measurementRegion: MeasurementRegion) {
  if (!isApiEnabled('profile')) return;
  await profileApi.updateMe({
    preferredLocale: locale,
    measurementRegion,
  });
}

export const useLocaleStore = create<LocaleState>((set, get) => ({
  locale: getStoredLocale(),
  measurementRegion: getStoredMeasurementRegion(),
  hydrated: false,
  saving: false,
  error: null,

  applyProfile: (profile) => {
    const current = get();
    const locale = normalizeLocale(profile.preferredLocale ?? current.locale ?? DEFAULT_LOCALE);
    const measurementRegion = normalizeMeasurementRegion(
      profile.measurementRegion ?? current.measurementRegion ?? DEFAULT_MEASUREMENT_REGION,
    );
    persistLocal(locale, measurementRegion);
    set({ locale, measurementRegion, hydrated: true, error: null });
  },

  loadFromProfile: async () => {
    if (!isApiEnabled('profile')) {
      set({ hydrated: true, error: null });
      persistLocal(get().locale, get().measurementRegion);
      return;
    }
    try {
      const profile = await profileApi.getMe();
      get().applyProfile(profile);
    } catch {
      set({ hydrated: true });
      persistLocal(get().locale, get().measurementRegion);
    }
  },

  setLocale: async (nextLocale) => {
    const previous = {
      locale: get().locale,
      measurementRegion: get().measurementRegion,
    };
    const locale = normalizeLocale(nextLocale);
    const measurementRegion = previous.measurementRegion;

    persistLocal(locale, measurementRegion);
    set({ locale, measurementRegion, saving: true, error: null });

    try {
      await syncProfile(locale, measurementRegion);
      set({ saving: false, hydrated: true });
    } catch {
      persistLocal(previous.locale, previous.measurementRegion);
      set({
        ...previous,
        saving: false,
        error: 'settings.locale.error',
      });
    }
  },

  setMeasurementRegion: async (nextRegion) => {
    const previous = {
      locale: get().locale,
      measurementRegion: get().measurementRegion,
    };
    const locale = previous.locale;
    const measurementRegion = normalizeMeasurementRegion(nextRegion);

    persistLocal(locale, measurementRegion);
    set({ locale, measurementRegion, saving: true, error: null });

    try {
      await syncProfile(locale, measurementRegion);
      set({ saving: false, hydrated: true });
    } catch {
      persistLocal(previous.locale, previous.measurementRegion);
      set({
        ...previous,
        saving: false,
        error: 'settings.locale.error',
      });
    }
  },
}));
